import { useEffect, useRef } from 'react';

export function useAlertSound(shouldPlay: boolean, isDashboardIntruder?: boolean) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const audioSrc = isDashboardIntruder ? '/audio/intruder.mp3' : '/audio/rizz.mp3';

    // Initialize or update audio element if source changes
    if (!audioRef.current || audioRef.current.src !== window.location.origin + audioSrc) {
      if (audioRef.current) {
        audioRef.current.pause(); // Stop previous sound if any
      }
      audioRef.current = new Audio(audioSrc);
      audioRef.current.loop = isDashboardIntruder ?? false;
    }

    const playSound = async () => {
      if (audioRef.current) {
        try {
          audioRef.current.currentTime = 0;
          await audioRef.current.play();
        } catch (error) {
          if (error instanceof Error && error.name !== 'NotAllowedError') {
            console.error('Error playing alert sound:', error);
          }
        }
      }
    };

    if (shouldPlay && audioRef.current) {
      playSound(); // Play immediately
      // Clear existing interval before setting a new one to prevent multiple intervals
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      intervalRef.current = setInterval(playSound, 30000); // Set up interval for repeated playing
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    }

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      // Check audioRef.current again as it might have been changed or nulled
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, [shouldPlay, isDashboardIntruder]);
}
